import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Share2, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Users,
  DollarSign,
  Activity,
  Info,
  CheckCircle2
} from 'lucide-react';
import { mockMarkets } from '../utils/mockData';
import { formatNumber, formatDistanceToNow } from '../utils/format';

export function MarketDetail() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'orderbook' | 'trades'>('overview');
  const [tradeAmount, setTradeAmount] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState<'yes' | 'no' | null>(null);

  const market = mockMarkets.find(m => m.id === id);

  if (!market) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Market not found</h1>
          <Link to="/markets" className="text-indigo-400 hover:text-indigo-300">
            Back to markets
          </Link>
        </div>
      </div>
    );
  }

  const yesOutcome = market.outcomes[0];
  const noOutcome = market.outcomes[1];
  const yesPercent = Math.round(yesOutcome.probability * 100);
  const noPercent = Math.round(noOutcome.probability * 100);

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="glass sticky top-16 z-30 border-b border-omni-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/markets">
              <motion.button
                whileHover={{ x: -4 }}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>
            </Link>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                <span className="capitalize">{market.category}</span>
                <span>•</span>
                <span className="text-omni-success">{market.status}</span>
              </div>
              <h1 className="text-xl font-bold text-white">{market.title}</h1>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Chart / Probability Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">Market Probability</h2>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  Ends {formatDistanceToNow(market.endTime)}
                </div>
              </div>

              {/* Probability Bars */}
              <div className="space-y-4">
                <div
                  onClick={() => setSelectedOutcome('yes')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedOutcome === 'yes' 
                      ? 'border-omni-success bg-omni-success/10' 
                      : 'border-omni-border hover:border-omni-success/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-omni-success" />
                      <span className="font-medium text-white">{yesOutcome.name}</span>
                    </div>
                    <span className="text-2xl font-bold text-omni-success">{yesPercent}%</span>
                  </div>
                  
                  <div className="h-3 bg-omni-border rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${yesPercent}%` }}
                      transition={{ duration: 1 }}
                      className="h-full bg-gradient-to-r from-omni-success to-green-400 rounded-full"
                    />
                  </div>
                </div>

                <div
                  onClick={() => setSelectedOutcome('no')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedOutcome === 'no' 
                      ? 'border-omni-danger bg-omni-danger/10' 
                      : 'border-omni-border hover:border-omni-danger/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-omni-danger" />
                      <span className="font-medium text-white">{noOutcome.name}</span>
                    </div>
                    <span className="text-2xl font-bold text-omni-danger">{noPercent}%</span>
                  </div>
                  
                  <div className="h-3 bg-omni-border rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${noPercent}%` }}
                      transition={{ duration: 1 }}
                      className="h-full bg-gradient-to-r from-omni-danger to-red-400 rounded-full"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Market Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-lg font-semibold text-white mb-4">About this Market</h2>
              <p className="text-gray-400 leading-relaxed">{market.description}</p>
              
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-omni-card rounded-xl">
                  <DollarSign className="w-5 h-5 text-indigo-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Volume</p>
                  <p className="text-lg font-semibold text-white">${formatNumber(market.totalVolume)}</p>
                </div>
                
                <div className="text-center p-4 bg-omni-card rounded-xl">
                  <Activity className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Liquidity</p>
                  <p className="text-lg font-semibold text-white">${formatNumber(market.totalLiquidity)}</p>
                </div>
                
                <div className="text-center p-4 bg-omni-card rounded-xl">
                  <Users className="w-5 h-5 text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Traders</p>
                  <p className="text-lg font-semibold text-white">{formatNumber(1234)}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Trading Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="glass rounded-2xl p-6 sticky top-32">
              <h3 className="text-lg font-semibold text-white mb-4">Place Trade</h3>

              {selectedOutcome ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-omni-card rounded-xl">
                    <span className="text-gray-400">Selected</span>
                    <span className={`font-medium ${
                      selectedOutcome === 'yes' ? 'text-omni-success' : 'text-omni-danger'
                    }`}>
                      {selectedOutcome === 'yes' ? yesOutcome.name : noOutcome.name}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Amount (USDC)</label>
                    <input
                      type="number"
                      value={tradeAmount}
                      onChange={(e) => setTradeAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-4 py-3 bg-omni-card border border-omni-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div className="p-4 bg-omni-card rounded-xl space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Price</span>
                      <span className="text-white">${selectedOutcome === 'yes' ? yesOutcome.price : noOutcome.price}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Potential Return</span>
                      <span className="text-omni-success">
                        +{selectedOutcome === 'yes' ? ((1/yesOutcome.price - 1) * 100).toFixed(1) : ((1/noOutcome.price - 1) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Fee (0.5%)</span>
                      <span className="text-white">${tradeAmount ? (parseFloat(tradeAmount) * 0.005).toFixed(2) : '0.00'}</span>
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-3 rounded-xl font-semibold text-white ${
                      selectedOutcome === 'yes' 
                        ? 'bg-omni-success hover:bg-green-600' 
                        : 'bg-omni-danger hover:bg-red-600'
                    }`}
                  >
                    Buy {selectedOutcome === 'yes' ? 'Yes' : 'No'}
                  </motion.button>

                  <button
                    onClick={() => setSelectedOutcome(null)}
                    className="w-full py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Info className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Select an outcome above to start trading</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
